import { Injectable } from '@angular/core';
import {
  WooCommercePaymentField,
  WooCommercePaymentMethod
} from '../../../interfaces/woo-commerce/api/payments/payments-api.interface';
import { map, Observable } from 'rxjs';
import { BaseWooCommerceV2ApiService } from './base-woo-commerce-v2-api.service';
import { HappyAvailablePaymentMethod } from '../../../interfaces/happy-api/happy-api.interface';
import { WooCommercePaymentFieldType } from '../../../enums/woo-commerce/api/payments/payments-api.enum';

const GATEWAY_FIELDS_CONFIG: Record<string, { id: string; title: string; description: string }> = {
  stripe: {
    id: 'stripe',
    title: 'Credit Card',
    description: 'Pay securely with your credit or debit card via Stripe.'
  },
  authorize_net: {
    id: 'authorize_net',
    title: 'Credit Card',
    description: 'Pay securely with your credit or debit card via Authorize.Net.'
  },
  paypal: {
    id: 'paypal',
    title: 'PayPal',
    description: 'Pay with your PayPal account.'
  },
  cash: {
    id: 'cash',
    title: 'Cash',
    description: 'Pay with cash on delivery or pickup.'
  }
};

/**
 * Per Cart-Subs.md §11 – POST /cart/checkout payment_method_data shapes:
 *  - Stripe:       { payment_method: "pm_xxx" }
 *  - Authorize.Net: { card_number, expiration_date, card_code }
 *  - PayPal:       {} (no fields)
 */
const GATEWAY_PAYMENT_FIELDS: Record<string, WooCommercePaymentField[]> = {
  stripe: [],
  authorize_net: [
    {
      type: WooCommercePaymentFieldType.Text,
      name: 'card_number',
      label: 'Card Number',
      description: 'Credit or debit card number',
      placeholder: 'Card Number'
    },
    {
      type: WooCommercePaymentFieldType.Text,
      name: 'expiration_date',
      label: 'Expiration Date',
      description: 'Card expiration date',
      placeholder: 'MM/YYYY'
    },
    {
      type: WooCommercePaymentFieldType.Text,
      name: 'card_code',
      label: 'Security Code',
      description: 'Card verification code',
      placeholder: 'CVV'
    }
  ]
};

@Injectable({ providedIn: 'root' })
export class WooCommercePaymentApiService extends BaseWooCommerceV2ApiService {
  private readonly happyPaymentUrl: string = '/payment-methods/available';

  getPaymentMethods(): Observable<WooCommercePaymentMethod[]> {
    return this.get<any>(this.happyPaymentUrl).pipe(
      map((response: any) => {
        const methods: HappyAvailablePaymentMethod[] =
          response?.data || response || [];
        if (!Array.isArray(methods)) return [];

        return methods.map((m) => this.mapToWooPaymentMethod(m));
      })
    );
  }

  private mapToWooPaymentMethod(
    method: HappyAvailablePaymentMethod
  ): WooCommercePaymentMethod {
    const config = GATEWAY_FIELDS_CONFIG[method.gateway] ?? {
      id: method.gateway,
      title: method.display_name,
      description: ''
    };

    return {
      id: config.id,
      title: method.display_name || config.title,
      method_title: method.display_name || config.title,
      description: config.description,
      fields: GATEWAY_PAYMENT_FIELDS[method.gateway] ?? [],
      gateway: method.gateway,
      supports_tokenization: method.supports_tokenization ?? false,
      ...(method.publishable_key ? { publishable_key: method.publishable_key } : {})
    };
  }
}
